import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { uniqueId } from 'lodash-es'

import { ProviderTaskType } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { actions as mixpanelActions, mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { selfHarmTasksStates } from '../../../common/constants/appConstants'
import { getAuthUserId } from '../../../data/auth/authSelectors'
import { useAppDispatch } from '../../../data/store'
import { setToastContent } from '../../../lyraTherapy/data/ltToastAutoActions'
import {
  useActivateRiskFlagMutation,
  useLazyGetClientEpisodesQuery,
} from '../../../lyraTherapy/episodes/data/episodesApi'
import { usePatchProviderTaskMutation } from '../../../providers/dashboard/data/providerTasksApi'

type MarkRiskAddressedArgs = {
  taskId: string
  taskType: ProviderTaskType
  clientId: string
}

export const useMarkRiskAddressed = () => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const providerId = useSelector(getAuthUserId)
  const [patchProviderTask, { isLoading: isPatchingTask }] = usePatchProviderTaskMutation()
  const [activateRiskFlag, { isLoading: isActivatingFlag }] = useActivateRiskFlagMutation()
  const [getClientEpisodes] = useLazyGetClientEpisodesQuery()

  const markRiskAddressed = useCallback(
    async ({ taskId, taskType, clientId }: MarkRiskAddressedArgs, keepBadgeVisible: boolean): Promise<boolean> => {
      track({
        event: mixpanelEvents.BUTTON_PRESS,
        action: mixpanelActions.OPEN_ITEMS_RISK_ALERT_MARK_ADDRESSED,
        properties: { task_type: taskType },
      })
      try {
        await patchProviderTask({
          id: taskId,
          kind: taskType,
          data: { status: selfHarmTasksStates.ACKNOWLEDGED, acknowledged_datetime: new Date().toISOString() },
        }).unwrap()
      } catch {
        dispatch(
          setToastContent({
            text: intl.formatMessage({
              defaultMessage: 'Failed to mark risk as addressed. Please try again.',
              description: 'Error message when marking risk addressed fails',
            }),
            id: uniqueId('mark-addressed-error'),
            toastType: 'error',
          }),
        )
        return false
      }
      if (keepBadgeVisible) {
        try {
          const episodes = await getClientEpisodes({
            patientId: clientId,
            providerId,
          }).unwrap()
          const episodeId = episodes?.[0]?.id ?? null
          if (episodeId) {
            await activateRiskFlag({ episodeId, flagType: taskType })
          }
        } catch {
          dispatch(
            setToastContent({
              text: intl.formatMessage({
                defaultMessage: 'Failed to activate risk badge. Please try again.',
                description: 'Error message when activating risk badge fails',
              }),
              id: uniqueId('activate-risk-badge-error'),
              toastType: 'error',
            }),
          )
        }
      }
      return true
    },
    [activateRiskFlag, dispatch, getClientEpisodes, intl, patchProviderTask, providerId],
  )

  return { markRiskAddressed, isLoading: isPatchingTask || isActivatingFlag }
}
